import {
  CanonicalPageHeader,
  InfoPanel,
  PageActions,
  QuickActionCard,
  StatusBadge,
  SummaryList,
  SummaryListItem,
  Tabs,
} from '@urbanik/ui';
import { BadgeDollarSign, Eye, Info, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { IndividualObligationForm } from './IndividualObligationForm';
import { ObligationBatchPreview } from './ObligationBatchPreview';
import { ObligationPortfolioView } from './ObligationPortfolioView';
import './obligations.css';

type ObligationSection = 'individual' | 'preview' | 'portfolio';

export function ObligationModelView({
  canCreate,
  canConfirmBatch = false,
  canReadPortfolio = false,
}: {
  canCreate: boolean;
  canConfirmBatch?: boolean;
  canReadPortfolio?: boolean;
}) {
  const [section, setSection] = useState<ObligationSection>('individual');
  return (
    <div className="shell-page obligation-model-page">
      <CanonicalPageHeader
        title="Obligaciones"
        description="Crea obligaciones individuales y prepara grupos con una vista previa explicable antes de cualquier generación masiva."
        actions={
          <PageActions>
            <StatusBadge tone="info">M06 · USD</StatusBadge>
            <StatusBadge tone="success">Operación auditada</StatusBadge>
          </PageActions>
        }
      />
      <Tabs
        label="Secciones de obligaciones"
        activeId={section}
        onChange={(id) => setSection(id as ObligationSection)}
        items={[
          {
            id: 'individual',
            label: 'Obligación individual',
            icon: <BadgeDollarSign aria-hidden="true" />,
          },
          { id: 'preview', label: 'Vista previa grupal', icon: <Eye aria-hidden="true" /> },
          ...(canReadPortfolio
            ? [{ id: 'portfolio', label: 'Cartera', icon: <ListChecks aria-hidden="true" /> }]
            : []),
        ]}
      />
      <div className="obligation-workspace">
        <main className="obligation-workspace__main">
          {section === 'individual' ? <IndividualObligationForm canCreate={canCreate} /> : null}
          {section === 'preview' ? <ObligationBatchPreview canConfirm={canConfirmBatch} /> : null}
          {section === 'portfolio' ? <ObligationPortfolioView /> : null}
        </main>
        <aside className="obligation-workspace__rail" aria-label="Resumen de obligaciones">
          <SummaryList title="Resumen del módulo">
            <SummaryListItem label="Moneda" value="USD" />
            <SummaryListItem label="Período" value="Mes de exigibilidad" />
            <SummaryListItem
              label="Creación individual"
              value="Confirmada y auditada"
              tone="success"
            />
            <SummaryListItem label="Vista previa grupal" value="Sin escrituras" tone="warning" />
          </SummaryList>
          <section className="obligation-quick-actions" aria-labelledby="obligation-actions-title">
            <h2 id="obligation-actions-title">Acciones rápidas</h2>
            <QuickActionCard
              icon={<BadgeDollarSign aria-hidden="true" />}
              title="Crear individual"
              description="Una obligación para un socio"
              onClick={() => setSection('individual')}
            />
            <QuickActionCard
              icon={<Eye aria-hidden="true" />}
              title="Preparar grupo"
              description="Incluidos, excluidos y razones"
              onClick={() => setSection('preview')}
            />
          </section>
          <InfoPanel title="Información" icon={<Info aria-hidden="true" />} tone="info">
            La vista previa no crea obligaciones. La confirmación masiva permanece separada para
            proteger la auditoría.
          </InfoPanel>
        </aside>
      </div>
    </div>
  );
}
